import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);
  const [initialized, setInitialized] = useState(false);
  const fetchingRef = useRef(false);

  const fetchUser = useCallback(async (currentToken) => {
    // Prevent concurrent fetches
    if (fetchingRef.current) return;
    fetchingRef.current = true;
    
    try {
      const response = await axios.get(`${API}/auth/me`, {
        headers: { 'Authorization': `Bearer ${currentToken}` }
      });
      setUser(response.data);
      axios.defaults.headers.common['Authorization'] = `Bearer ${currentToken}`;
    } catch (error) {
      console.error('Error fetching user:', error);
      // Only logout on 401 (unauthorized), not on network errors
      if (error.response?.status === 401) {
        localStorage.removeItem('token');
        delete axios.defaults.headers.common['Authorization'];
        setToken(null);
        setUser(null);
      }
      // For other errors (network, 500, etc.), keep the token and user state
      // This prevents random logouts on temporary network issues
    } finally {
      fetchingRef.current = false;
      setLoading(false);
      setInitialized(true);
    }
  }, []);

  useEffect(() => {
    const storedToken = localStorage.getItem('token');
    if (storedToken) {
      fetchUser(storedToken);
    } else {
      setLoading(false);
      setInitialized(true);
    }
  }, []); // Run only once on mount

  // Handle token changes from login/logout
  useEffect(() => {
    if (initialized && token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    }
  }, [token, initialized]);

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    const { access_token, user: userData } = response.data;
    localStorage.setItem('token', access_token);
    axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
    setToken(access_token);
    setUser(userData);
    return userData;
  };

  const register = async (email, password, full_name) => {
    const response = await axios.post(`${API}/auth/register`, { email, password, full_name });
    const { access_token, user: userData } = response.data;
    localStorage.setItem('token', access_token);
    axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
    setToken(access_token);
    setUser(userData);
    return userData;
  };

  const logout = () => {
    localStorage.removeItem('token');
    delete axios.defaults.headers.common['Authorization'];
    setToken(null);
    setUser(null);
  };

  const hasModuleAccess = (module) => {
    if (!user) return false;
    if (user.role === 'admin' || user.role === 'superadmin') return true;
    return user.modules_access?.includes(module) || false;
  };

  const isAdmin = () => user?.role === 'admin' || user?.role === 'superadmin';
  const isSuperAdmin = () => user?.role === 'superadmin';

  return (
    <AuthContext.Provider value={{ user, token, loading, login, register, logout, hasModuleAccess, isAdmin, isSuperAdmin }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
